/** Sliding TTL for signed-in cart orders (status=cart). Checked on login / hydrate. */
export const CART_TTL_MS = 14 * 24 * 60 * 60 * 1000;

const TOUCH_STORAGE_PREFIX = "dido-cart-touched:";

function parseTimestampMs(raw: unknown): number | null {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    // Heuristic: values below ~1e12 are unix seconds.
    return raw < 1e12 ? Math.round(raw * 1000) : Math.round(raw);
  }
  if (typeof raw === "string" && raw.trim()) {
    const trimmed = raw.trim();
    if (/^\d+$/.test(trimmed)) {
      return parseTimestampMs(Number(trimmed));
    }
    const parsed = Date.parse(trimmed);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
}

/** Most recent activity timestamp on a cart order from common API fields. */
export function cartOrderActivityMs(order: Record<string, unknown>): number | null {
  const candidates = [
    order.last_updated,
    order.updated_at,
    order.created_at,
    order.date,
  ];
  let best: number | null = null;
  for (const c of candidates) {
    const ms = parseTimestampMs(c);
    if (ms == null) continue;
    if (best == null || ms > best) best = ms;
  }
  return best;
}

/**
 * True when the cart is older than {@link CART_TTL_MS}.
 * Uses max(order activity, local touch). No signals → keep (avoid nuking undated carts).
 */
export function isCartOrderExpired(
  order: Record<string, unknown>,
  now = Date.now(),
  localTouchMs?: number | null,
): boolean {
  const orderMs = cartOrderActivityMs(order);
  const activity = Math.max(orderMs ?? 0, localTouchMs ?? 0);
  if (activity <= 0) return false;
  return now - activity > CART_TTL_MS;
}

export function readCartTouchedAt(customerId: string): number | null {
  if (typeof window === "undefined" || !customerId.trim()) return null;
  try {
    const raw = localStorage.getItem(`${TOUCH_STORAGE_PREFIX}${customerId.trim()}`);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

/** Refresh sliding TTL (call on login when cart is kept, and after cart sync). */
export function touchCartTtl(customerId: string, at = Date.now()): void {
  if (typeof window === "undefined" || !customerId.trim()) return;
  try {
    localStorage.setItem(
      `${TOUCH_STORAGE_PREFIX}${customerId.trim()}`,
      String(at),
    );
  } catch {
    // ignore quota / private mode
  }
}

export function clearCartTtl(customerId: string): void {
  if (typeof window === "undefined" || !customerId.trim()) return;
  try {
    localStorage.removeItem(`${TOUCH_STORAGE_PREFIX}${customerId.trim()}`);
  } catch {
    // ignore
  }
}
